import { z } from 'zod'

import { ExamScheduleProgressSchema, ExamSchema, ExamTypeSchema } from '~/@entities/Exam'
import { ProblemSchema } from '~/@entities/Problem'
import {
  AllGradeSubjectSchema,
  HighSchoolSubjectSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { type ScoringItem, ScoringResultSchema } from '~/@entities/Scoring'
import { StudentIdSchema } from '~/@entities/Student'
import type { WorksheetScoringProblem } from '~/@entities/StudentWorksheet'
import { LearningStatusSchema } from '~/@entities/Worksheet'

// 서버는 Asia/Seoul LocalDateTime 을 타임존 없이 낸다.
const _LocalDateTimeSchema = z.iso.datetime({ local: true })

/** 시험 문항 하나의 채점 행 */
export const StudentExamScoringSchema = z.object({
  studentExamId: z.number(),
  examProblemId: z.number(),
  userAnswer: z.string().nullable(),
  // 다중 셀 답의 미입력 칸은 null 원소다. 단답 문항은 null
  userAnswers: z.array(z.string().nullable()).nullable(),
  result: ScoringResultSchema,
  handwrittenNoteUrl: z.string().nullable(),
  scoreDatetime: _LocalDateTimeSchema,
  teacherScoringFlag: z.boolean(),
})
export type StudentExamScoring = z.infer<typeof StudentExamScoringSchema>

/** 학생에게 배정된 시험 한 건의 진행 기록 */
export const StudentExamSchema = z.object({
  id: z.number(),
  examId: z.number(),
  studentId: StudentIdSchema,
  // 응시 전에는 점수가 없다
  score: z.number().nullable(),
  correctCount: z.number(),
  wrongCount: z.number(),
  status: LearningStatusSchema,
  assignDatetime: _LocalDateTimeSchema,
  openDatetime: _LocalDateTimeSchema.nullable(),
  scoringList: z.array(StudentExamScoringSchema),
})
export type StudentExam = z.infer<typeof StudentExamSchema>

/** 학생 시험 목록의 한 줄 — 시험 본체가 붙어 온다 */
export const StudentExamWithExamSchema = StudentExamSchema.extend({
  exam: ExamSchema,
})
export type StudentExamWithExam = z.infer<typeof StudentExamWithExamSchema>

/** 시험 응시 화면이 받는 배정 상세. 시험 본체 대신 종류·진행 상태·학생 이름만 붙어 온다 */
export const StudentExamAssignmentSchema = StudentExamSchema.extend({
  examType: ExamTypeSchema,
  scheduleProgress: ExamScheduleProgressSchema,
  studentName: z.string(),
})
export type StudentExamAssignment = z.infer<typeof StudentExamAssignmentSchema>

/***************** 수상 등급 *****************/
// 서버는 enum 없는 문자열 칸(StudentExamEntity.tier)으로 낸다. 수상 5종(KmmAwardTier)에 미수상·고등을 더해 받고,
// 미응시는 null 이다.
export const AWARD_TIER = [
  'GRAND_PRIZE', // 대상
  'GOLD_AWARD', // 금상
  'SILVER_AWARD', // 은상
  'BRONZE_AWARD', // 동상
  'ENCOURAGEMENT_AWARD', // 장려상
  'NOT_AWARDED', // 미수상
  'HIGH_SCHOOL', // 고등부는 평가 기준이 달라 수상하지 않는다
] as const
export const AwardTierSchema = z.enum(AWARD_TIER)
export type AwardTier = z.infer<typeof AwardTierSchema>

/***************** 선생님 채점 진행 *****************/
export const STUDENT_EXAM_SCORING_STATUS = ['NONE', 'IN_PROGRESS', 'COMPLETED'] as const
export const StudentExamScoringStatusSchema = z.enum(STUDENT_EXAM_SCORING_STATUS)
export type StudentExamScoringStatus = z.infer<typeof StudentExamScoringStatusSchema>

/** 채점 요약의 문항 한 줄 — 문제 id 와 배점이 함께 온다 */
export const StudentExamScoringSummaryItemSchema = StudentExamScoringSchema.extend({
  problemId: ProblemSchema.shape.id,
  score: z.number(),
})
export type StudentExamScoringSummaryItem = z.infer<typeof StudentExamScoringSummaryItemSchema>

/** 학생 한 명의 시험 채점 요약 */
export const StudentExamScoringSummarySchema = StudentExamSchema.omit({
  id: true,
  scoringList: true,
}).extend({
  studentExamId: StudentExamSchema.shape.id,
  studentName: z.string(),
  solveDatetime: _LocalDateTimeSchema.nullable(),
  scoringList: z.array(StudentExamScoringSummaryItemSchema),
})
export type StudentExamScoringSummary = z.infer<typeof StudentExamScoringSummarySchema>

/** 선생님 채점 화면이 받는 시험 하나의 채점 요약. 문제는 시험지 순서대로 온다 */
export const ExamScoringSummarySchema = z.object({
  studentExamScoringSummary: z.array(StudentExamScoringSummarySchema),
  problems: z.array(ProblemSchema),
})
export type ExamScoringSummary = z.infer<typeof ExamScoringSummarySchema>

/** 회차 월 시험 목록이 학생마다 싣는 응시 결과 */
export const StudentExamResultSchema = z.object({
  id: StudentExamSchema.shape.id,
  studentId: StudentIdSchema,
  studentName: z.string(),
  status: LearningStatusSchema,
  score: z.number().nullable(),
  correctCount: z.number(),
  wrongCount: z.number(),
  tier: AwardTierSchema.nullable(),
})
export type StudentExamResult = z.infer<typeof StudentExamResultSchema>

const _ExamOfMonthSchema = z.object({
  id: ExamSchema.shape.id,
  type: ExamTypeSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  subject: HighSchoolSubjectSchema.nullable(),
})

/** 회차 월의 시험 한 종과 그 시험을 배정받은 학생들 */
export const ExamWithStudentExamsSchema = _ExamOfMonthSchema.extend({
  title: z.string(),
  // 시험지 내려받기·인쇄는 학습지 API 를 빌려 쓴다
  worksheetId: z.number(),
  fileUrl: z.string().nullable(),
  fileName: z.string().nullable(),
  studentExams: z.array(
    StudentExamResultSchema.extend({ scoringStatus: StudentExamScoringStatusSchema }),
  ),
})
export type ExamWithStudentExams = z.infer<typeof ExamWithStudentExamsSchema>

/** 회차 월 시험 한 종의 수상 결과 */
export const ExamAwardSchema = _ExamOfMonthSchema.extend({
  studentExams: z.array(StudentExamResultSchema),
})
export type ExamAward = z.infer<typeof ExamAwardSchema>

/** 시험 문항 하나의 채점. 시험 화면은 학습지 채점 화면을 재사용해 학습지 채점과 같은 모양으로 든다 */
export type StudentExamProblemScoring = ScoringItem<
  {
    kind: 'EXAM'
    id: StudentExamScoring['examProblemId']
    // 학생 답을 선생님이 고쳐 채점했는지
    teacherScoringFlag: boolean
  },
  WorksheetScoringProblem
>
